<template>
	<div class="accounting-chart">
		<!-- Header -->
		<div class="chart-header">
			<div class="header-content">
				<div class="header-icon">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2"
							stroke-linecap="round" stroke-linejoin="round" />
						<path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"
							stroke-linejoin="round" />
						<path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"
							stroke-linejoin="round" />
					</svg>
				</div>
				<div class="header-info">
					<h1 class="chart-titlee">Estado de Resultados Mensualizado {{ yearRange }}</h1>
					<div class="view-controls">
						<div class="view-toggle">
							<button @click="viewMode = 'single'"
								:class="['toggle-btn', { 'active': viewMode === 'single' }]">
								Vista Detallada
							</button>
							<button @click="viewMode = 'comparison'"
								:class="['toggle-btn', { 'active': viewMode === 'comparison' }]">
								Vista General
							</button>
						</div>
						<div class="expand-controls" v-if="viewMode === 'single'">
							<button @click="expandAll" class="control-btn">Expandir Todo</button>
							<button @click="collapseAll" class="control-btn">Suprimir Todo</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Year Tabs -->
		<div v-if="viewMode === 'single'" class="year-tabs">
			<button v-for="year in availableYears" :key="year" @click="setActiveYear(year)"
				:class="['year-tab-btn', { 'active': activeYear === year }]">
				{{ year }}
			</button>
		</div>

		<!-- Period Selector -->
		<div v-if="viewMode === 'single'"  class="period-controls">
			<div class="period-selector">
				<button v-for="period in periodsForActiveYear" :key="`${period.year}-${period.month}`"
					@click="setActivePeriod(period)" :class="['period-btn', { 'active': isActivePeriod(period) }]">
					{{ getMonthName(period.month) }} {{ period.year }}
				</button>
			</div>
		</div>

		<!-- Summary Cards -->
		<div class="summary-section">
			<div v-if="viewMode === 'single'" class="summary-cards">
				<div class="summary-card">
					<div class="summary-label">Período Actual</div>
					<div class="summary-value">{{ getMonthName(activePeriod.month) }} {{ activePeriod.year }}</div>
				</div>
				<div class="summary-card">
					<div class="summary-label">Total Cuentas Padres</div>
					<div class="summary-value">{{ getTotalAccounts(currentPeriodData) }}</div>
				</div>
				<div class="summary-card">
					<div class="summary-label">Ingresos</div>
					<div class="summary-value balance-credit">
						{{ formatCurrency(getIncomeBalance(currentPeriodData)) }}
					</div>
				</div>
				<div class="summary-card">
					<div class="summary-label">Gastos</div>
					<div class="summary-value balance-debit">
						{{ formatCurrency(getExpenseBalance(currentPeriodData)) }}
					</div>
				</div>
				<div class="summary-card">
					<div class="summary-label">Balance General</div>
					<div class="summary-value" :class="getBalanceClass(getTotalBalance(currentPeriodData))">
						{{ formatCurrency(getTotalBalance(currentPeriodData)) }}
					</div>
				</div>
			</div>
		</div>

		<!-- Accounts Display -->
		<div v-if="viewMode === 'single'" class="accounts-container">
			<div v-for="account in currentPeriodData" :key="account.id" class="account-card">
				<AccountNode :account="account" :level="0" :expanded-nodes="expandedNodes" @toggle="toggleNode" />
			</div>
		</div>

		<div v-else>
			<div>
				<ComparisonAccountNode  :accountsData="accountsData" />
			</div>
		</div>
	</div>
</template>

<script>
// Importar los componentes separados
import AccountNode from './account-node';
import ComparisonAccountNode from './comparison-account-node';

export default {
	name: 'AccountingChart',
	components: { AccountNode, ComparisonAccountNode },
	props: {
		accountsData: { type: Array, required: true, default: () => [] },
	},
	data() {
		return {
			expandedNodes: new Set(),
			activeYear: null,
			activePeriod: null,
			viewMode: 'single', // 'single' or 'comparison'
			comparisonPeriods: [
				{ month: 1, year: 2023 },
				{ month: 12, year: 2024 },
			],
		};
	},
	computed: {
		availableYears() {
			const years = [...new Set(this.accountsData.map(account => account.year))];
			return years.sort();
		},
		periodsForActiveYear() {
			return this.availablePeriods.filter(period => period.year === this.activeYear);
		},
		availablePeriods() {
			const periods = [];
			const seenPeriods = new Set();

			this.accountsData.forEach((account) => {
				const key = `${account.year}-${account.month}`;
				if (!seenPeriods.has(key)) {
					seenPeriods.add(key);
					periods.push({ month: account.month, year: account.year });
				}
			});

			return periods.sort((a, b) => {
				if (a.year !== b.year) return a.year - b.year;
				return a.month - b.month;
			});
		},
		currentPeriodData() {
			return this.getDataForPeriod(this.activePeriod);
		},
		yearRange() {
			if (this.availablePeriods.length === 0) return '';
			const startYear = this.availableYears[0];
			const endYear = this.availableYears[this.availableYears.length - 1];
			return startYear === endYear ? startYear : `${startYear} - ${endYear}`;
		},
	},
	mounted() {
		// Establecer el primer año disponible como activo
		if (this.availableYears.length > 0) {
			this.setActiveYear(this.availableYears[0]);
		}
	},
	methods: {
		getDataForPeriod(period) {
			return this.accountsData.filter(account =>
				account.month === period.month && account.year === period.year,
			);
		},
		expandMainNodes() {
			this.currentPeriodData.forEach((account) => {
				if (account.level === 1) {
					this.expandedNodes.add(account.id);
				}
			});
		},
		expandAll() {
			const expand = (accounts) => {
				const newSet = new Set(this.expandedNodes);
				accounts.forEach((account) => {
					newSet.add(account.id);
					if (account.children && account.children.length) {
						const childExpanded = expand(account.children);
						childExpanded.forEach(id => newSet.add(id));
					}
				});
				return newSet;
			};

			this.expandedNodes = expand(this.currentPeriodData);
		},
		collapseAll() {
			this.expandedNodes = new Set();
		},
		expandNodeRecursively(account) {
			this.expandedNodes.add(account.id);
			if (account.children) {
				account.children.forEach((child) => {
					this.expandNodeRecursively(child);
				});
			}
		},
		toggleNode(nodeId) {
			const newSet = new Set(this.expandedNodes);
			if (newSet.has(nodeId)) {
				newSet.delete(nodeId);
			} else {
				newSet.add(nodeId);
			}
			this.expandedNodes = newSet;
		},
		setActiveYear(year) {
			this.activeYear = year;
			const periods = this.availablePeriods.filter(period => period.year === year);
			this.activePeriod = periods.length > 0 ? periods[0] : null;
		},
		setActivePeriod(period) {
			this.activePeriod = period;
		},
		isActivePeriod(period) {
			return this.activePeriod && this.activePeriod.month ===
				period.month && this.activePeriod.year === period.year;
		},
		formatCurrency(amount) {
			return new Intl.NumberFormat('es-CO', {
				style: 'currency',
				currency: 'COP',
				minimumFractionDigits: 2,
			}).format(Math.abs(amount));
		},
		getMonthName(month) {
			const months = [
				'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
				'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
			];
			return months[month - 1] || 'Mes inválido';
		},
		getBalanceClass(balance) {
			if (balance > 0) return 'balance-debit';
			if (balance < 0) return 'balance-credit';
			return 'balance-neutral';
		},
		getTotalAccounts(data) {
			return data.length;
		},
		getTotalBalance(data) {
			return data.reduce((total, account) => total + (account.balance || 0), 0);
		},
		getIncomeBalance(data) {
			const incomeAccount = data.find(account => account.code === '4');
			return incomeAccount ? Math.abs(incomeAccount.balance) : 0;
		},
		getExpenseBalance(data) {
			const expenseAccounts = data.filter(account => account.code !== '4');
			console.log('expenseAccounts::: ', expenseAccounts);
			// Forma simplificada con return implícito
			const totalExpense = expenseAccounts.reduce((sum, account) =>
				sum + (account.balance || 0), 0);
			return totalExpense;
		},
		getComparisonData() {
			return this.getDataForPeriod(this.comparisonPeriods[0]);
		},
		getComparisonAccount(account) {
			const comparisonData = this.getDataForPeriod(this.comparisonPeriods[1]);
			return this.findAccountByCode(comparisonData, account.code);
		},
		findAccountByCode(accounts, code) {
			return accounts.find(account =>
				account.code === code ||
				(account.children && this.findAccountByCode(account.children, code)),
			) || null;
		},
		getBalanceDifference() {
			const balance1 = this.getTotalBalance(this.getDataForPeriod(this.comparisonPeriods[0]));
			const balance2 = this.getTotalBalance(this.getDataForPeriod(this.comparisonPeriods[1]));
			return balance2 - balance1;
		},
		getPercentageChange() {
			const balance1 = this.getTotalBalance(this.getDataForPeriod(this.comparisonPeriods[0]));
			const balance2 = this.getTotalBalance(this.getDataForPeriod(this.comparisonPeriods[1]));
			if (balance1 === 0) return balance2 === 0 ? 0 : 100;
			return (((balance2 - balance1) / Math.abs(balance1)) * 100).toFixed(1);
		},
	},
};
</script>

<style scoped lang="scss">
.accounting-chart {
	max-width: 1400px;
	margin: 0 auto;
	padding: 20px;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	min-height: 100vh;
}

/* Header Styles */
.chart-header {
	background: white;
	border-radius: 12px;
	padding: 24px;
	margin-bottom: 24px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
	border-left: 4px solid #02979B;
}

.header-content {
	display: flex;
	align-items: flex-start;
	margin-bottom: 20px;
}

.header-icon {
	background: #02979B;
	color: white;
	width: 48px;
	height: 48px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 16px;
	flex-shrink: 0;
}

.header-info {
	flex: 1;
}

.chart-titlee {
    font-size: 24px;        /* Tamaño por defecto (escritorio) */
    font-weight: 700;
    color: #1a202c;
    text-align: left;       /* Alineación consistente */
    background: white !important;
    margin: 0 0 16px 0;
    padding: 0 10px;        /* Pequeño padding para móviles */
    box-sizing: border-box; /* Evita desbordamientos */
}

/* Tablet (768px o menos) */
@media (max-width: 768px) {
    .chart-titlee {
        font-size: 20px;    /* Tamaño ligeramente menor */
    }
}

/* Móvil (480px o menos) */
@media (max-width: 480px) {
    .chart-titlee {
        font-size: 12px;    /* Tamaño más pequeño para móviles */
        margin-bottom: 12px;
        padding: 0 8px;     /* Ajuste de padding */
    }
}

.period-controls {
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 15px !important;
}

.period-selector {
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
	max-width: 100%;
	overflow-x: auto;
	padding: 4px 0;
}

.period-btn {
	padding: 6px 12px;
	border: 2px solid #e2e8f0;
	background: white;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	white-space: nowrap;
	min-width: 80px;
}

.summary-section {
	margin-bottom: 15px !important;
}

.period-btn:hover {
	border-color: #02979B;
	background: #f0fdfa;
}

.period-btn.active {
	border-color: #02979B;
	background: #02979B;
	color: white;
}

.view-controls {
	display: flex;
	gap: 12px;
	align-items: center;
	flex-wrap: wrap;
}

.view-toggle {
	display: flex;
	gap: 4px;
	background: #f1f5f9;
	padding: 4px;
	border-radius: 8px;
}

.toggle-btn {
	padding: 8px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.toggle-btn.active {
	background: #02979B;
	color: white;
}

.expand-controls {
	display: flex;
	gap: 6px;
}

.control-btn {
	padding: 6px 12px;
	border: 1px solid #02979B;
	background: white;
	color: #02979B;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.control-btn:hover {
	background: #02979B;
	color: white;
}

/* Summary Styles */
.summary-cards {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 12px;
}

.summary-card {
	background: #f7fafc;
	padding: 16px;
	border-radius: 8px;
	border-left: 3px solid #02979B;
}

.summary-label {
	font-size: 12px;
	color: #718096;
	text-transform: uppercase;
	font-weight: 600;
	margin-bottom: 4px;
}

.summary-value {
	font-size: 16px;
	font-weight: 700;
	color: #1a202c;
}

/* Comparison Summary */
.comparison-summary {
	display: grid;
	grid-template-columns: 1fr auto 1fr auto;
	gap: 16px;
	align-items: center;
}

.comparison-card {
	background: #f7fafc;
	padding: 16px;
	border-radius: 8px;
	border-left: 3px solid #02979B;
}

.comparison-card h3 {
	font-size: 16px;
	font-weight: 600;
	margin: 0 0 12px 0;
	color: #1a202c;
}

.comparison-stats {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.stat {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.stat-label {
	font-size: 12px;
	color: #718096;
	font-weight: 500;
}

.comparison-arrow {
	color: #02979B;
	font-size: 24px;
	font-weight: bold;
	text-align: center;
}

.difference-card {
	background: #02979B;
	color: white;
	padding: 16px;
	border-radius: 8px;
	text-align: center;
}

.difference-label {
	font-size: 12px;
	opacity: 0.9;
	display: block;
	margin-bottom: 4px;
}

.difference-percentage {
	font-size: 12px;
	opacity: 0.8;
	margin-top: 4px;
}

/* Account Styles */
.accounts-container,
.comparison-container {
	display: grid;
	gap: 16px;
}

.account-card,
.comparison-account-card {
	background: white;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
	border: 1px solid #e2e8f0;
}

.balance-credit {
	color: #02979B;
}

.balance-debit {
	color: #e53e3e;
}

.balance-neutral {
	color: #718096;
}

/* Year Tabs Styles */
.year-tabs {
	display: flex;
	gap: 8px;
	margin-bottom: 16px;
}

.year-tab-btn {
	padding: 8px 16px;
	border: 1px solid #02979B;
	background: white;
	color: #02979B;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.year-tab-btn:hover {
	background: #02979B;
	color: white;
}

.year-tab-btn.active {
	background: #02979B;
	color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
	.accounting-chart {
		padding: 12px;
	}

	.chart-header {
		padding: 16px;
	}

	.header-content {
		flex-direction: column;
		align-items: flex-start;
	}

	.header-icon {
		margin-bottom: 12px;
		margin-right: 0;
	}

	.comparison-summary {
		grid-template-columns: 1fr;
		gap: 12px;
	}

	.period-controls {
		width: 100%;
	}

	.view-controls {
		flex-direction: column;
		align-items: flex-start;
	}

	.summary-cards {
		grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	}
}
</style>


// WEBPACK FOOTER //
// src/components/records/balance-sheet-month.vue